import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '../ui';
import { CourseDetailTab } from '../../apis/api-client';
import { TiptapEditor } from '../common';
import { DeleteIcon, EditIcon, DragIndicatorIcon } from '../icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface CourseDetailTabsEditorProps {
  tabs: CourseDetailTab[];
  onTabsChange: (next: CourseDetailTab[]) => void;
}

/**
 * Pure controlled tabs editor. Parent owns the array; this component never
 * talks to the server. The full snapshot ships with the course save and the
 * server reconciles by id (positive ids = update, id===0 = create, missing
 * rows = soft-delete). Negative ids that may appear here for unsaved tabs
 * are local-only — the server treats them as id===0 (create) on save.
 */
export default function CourseDetailTabsEditor({ tabs, onTabsChange }: CourseDetailTabsEditorProps) {
  const [editingTab, setEditingTab] = useState<CourseDetailTab | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const localIdRef = useRef(-1);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleAdd = () => {
    setEditingTab({ id: 0, title: '', contentHtml: '' });
    setShowEditor(true);
  };

  const handleEdit = (tab: CourseDetailTab) => {
    setEditingTab({ ...tab });
    setShowEditor(true);
  };

  const handleDelete = (tab: CourseDetailTab) => {
    if (!window.confirm(`Delete tab "${tab.title}"?`)) return;
    onTabsChange(tabs.filter((t) => t.id !== tab.id));
  };

  const handleSaveTab = (saved: CourseDetailTab) => {
    if (saved.id != null && saved.id !== 0) {
      // Replace existing entry (matched by id, including negative locals)
      onTabsChange(tabs.map((t) => (t.id === saved.id ? saved : t)));
    } else {
      // New tab → assign a local negative id so the dnd list has a stable key.
      // Server gets id===0 on save and treats it as a create.
      const localId = localIdRef.current--;
      onTabsChange([...tabs, { ...saved, id: localId }]);
    }
    setShowEditor(false);
    setEditingTab(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = tabs.findIndex((t) => `tab-${t.id}` === active.id);
    const newIndex = tabs.findIndex((t) => `tab-${t.id}` === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    onTabsChange(arrayMove(tabs, oldIndex, newIndex));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Custom tabs
        </Typography>
        <Button size="small" variant="contained" color="primary" onClick={handleAdd}>
          Add Tab
        </Button>
      </Box>

      {tabs.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No tabs yet. Tabs render as stacked sections on the public detail page.
        </Typography>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={tabs.map((t) => `tab-${t.id}`)} strategy={verticalListSortingStrategy}>
          {tabs.map((tab) => (
            <SortableTabRow
              key={tab.id}
              tab={tab}
              onEdit={() => handleEdit(tab)}
              onDelete={() => handleDelete(tab)}
            />
          ))}
        </SortableContext>
      </DndContext>

      {editingTab && (
        <TabEditorDialog
          open={showEditor}
          tab={editingTab}
          onClose={() => {
            setShowEditor(false);
            setEditingTab(null);
          }}
          onSave={handleSaveTab}
        />
      )}
    </Box>
  );
}

interface SortableTabRowProps {
  tab: CourseDetailTab;
  onEdit: () => void;
  onDelete: () => void;
}

function SortableTabRow({ tab, onEdit, onDelete }: SortableTabRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `tab-${tab.id}`,
  });

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 1,
        px: 1.5,
        mb: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Box {...attributes} {...listeners} sx={{ cursor: 'grab', display: 'flex' }}>
        <DragIndicatorIcon sx={{ color: 'grey.500' }} />
      </Box>
      <Typography sx={{ flex: 1 }}>{tab.title || '(untitled)'}</Typography>
      <IconButton size="small" onClick={onEdit}>
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={onDelete}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

interface TabEditorDialogProps {
  open: boolean;
  tab: CourseDetailTab;
  onClose: () => void;
  onSave: (tab: CourseDetailTab) => void;
}

function TabEditorDialog({ open, tab, onClose, onSave }: TabEditorDialogProps) {
  const [title, setTitle] = useState(tab.title || '');
  const [contentHtml, setContentHtml] = useState(tab.contentHtml || '');
  const initRef = useRef(tab.id);

  useEffect(() => {
    if (initRef.current !== tab.id || !open) {
      setTitle(tab.title || '');
      setContentHtml(tab.contentHtml || '');
      initRef.current = tab.id;
    }
  }, [tab, open]);

  const handleSave = () => {
    if (!title.trim()) return;
    onSave({ ...tab, title: title.trim(), contentHtml });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{tab.id != null && tab.id > 0 ? 'Edit Tab' : 'Add Tab'}</DialogTitle>
      <DialogContent dividers>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          autoFocus
          sx={{ mb: 2, mt: 1 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Content
        </Typography>
        <TiptapEditor content={contentHtml} onChange={setContentHtml} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={!title.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
